import React, { useEffect, useState, useCallback } from 'react';
import api from '../lib/api';
import { Clock, ChefHat, Wine, Flame, Target, HandPlatter, Check } from 'lucide-react';
import { Badge } from './ui/badge';
import { tableLabel } from '../lib/tableLabel';

/**
 * Read-only overview of EVERY active order across all panels. Mounted on the
 * Kitchen / Bar / Shisha / Serving panels when the staff member toggles "All
 * Live Orders" on. Lets a chef glance at what bar and shisha are doing for
 * the same table without leaving their own panel. Action buttons are NOT
 * rendered here — the staff must switch back to "My Panel" view to advance
 * items. This prevents accidental cross-panel taps and keeps audit logs clean.
 */

const CAT_META = {
  food:       { Icon: ChefHat,     label: 'Food',   color: 'text-gold',       bg: 'bg-gold/10 border-gold/20' },
  drinks:     { Icon: Wine,        label: 'Drinks', color: 'text-blue-accent',bg: 'bg-blue-accent/10 border-blue-accent/20' },
  shisha:     { Icon: Flame,       label: 'Shisha', color: 'text-amber-400',  bg: 'bg-amber-500/10 border-amber-500/20' },
  activities: { Icon: Target,      label: 'Play',   color: 'text-emerald-400',bg: 'bg-emerald-500/10 border-emerald-500/20' },
  other:      { Icon: HandPlatter, label: 'Other',  color: 'text-zinc-400',   bg: 'bg-zinc-500/10 border-zinc-500/20' },
};

const STATUS_META = {
  received:  { label: 'Received',  cls: 'bg-blue-accent/15 text-blue-accent' },
  preparing: { label: 'Preparing', cls: 'bg-amber-500/15 text-amber-400' },
  ready:     { label: 'Ready',     cls: 'bg-emerald-500/15 text-emerald-400' },
  served:    { label: 'Served',    cls: 'bg-zinc-500/15 text-zinc-400' },
};

function catKey(item) {
  const c = item.prep_panel || item.category || '';
  if (c === 'food' || c === 'kitchen') return 'food';
  if (c === 'drinks' || c === 'bar' || c === 'bartender') return 'drinks';
  if (c === 'shisha') return 'shisha';
  if (c === 'activities') return 'activities';
  return 'other';
}

function getElapsedMinutes(ts) {
  return Math.floor((Date.now() - new Date(ts).getTime()) / 60000);
}

export default function AllLiveOrdersView({ myPanelKey }) {
  const [orders, setOrders] = useState([]);
  const [err, setErr] = useState('');

  const fetchAll = useCallback(async () => {
    try {
      // /orders?status=active returns every paid + active order across the
      // restaurant — perfect for the cross-panel glance view. Customers and
      // takeaway are both included.
      const { data } = await api.get('/orders?status=active');
      setOrders(data);
      setErr('');
    } catch (e) {
      setErr(e?.response?.data?.detail || 'Failed to load orders');
    }
  }, []);

  useEffect(() => {
    fetchAll();
    const i = setInterval(fetchAll, 5000);
    const onVis = () => { if (document.visibilityState === 'visible') fetchAll(); };
    document.addEventListener('visibilitychange', onVis);
    window.addEventListener('focus', fetchAll);
    return () => {
      clearInterval(i);
      document.removeEventListener('visibilitychange', onVis);
      window.removeEventListener('focus', fetchAll);
    };
  }, [fetchAll]);

  // Filter out orders where every item is already served (cosmetic — those
  // technically auto-complete server-side, but until polling catches up we
  // don't want them lingering on the all-orders board).
  const visible = orders.filter(o => (o.items || []).some(i => i.status !== 'served'));

  if (err) {
    return (
      <div className="p-6 text-center text-red-400 font-sans text-sm" data-testid="all-orders-error">{err}</div>
    );
  }
  if (visible.length === 0) {
    return (
      <div className="p-12 text-center text-zinc-500 font-sans" data-testid="all-orders-empty">
        Nothing live right now.
      </div>
    );
  }

  return (
    <div className="p-4">
      <p className="text-[11px] uppercase tracking-[0.2em] text-zinc-500 font-sans font-semibold mb-3" data-testid="all-orders-banner">
        All live orders · read-only · switch to My Panel to take actions
      </p>
      <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4 stagger-children">
        {visible.map(order => {
          // Group items by category so each card visually mirrors the panel
          // they're being prepared on.
          const groups = {};
          for (const it of order.items || []) {
            const k = catKey(it);
            (groups[k] = groups[k] || []).push(it);
          }
          // Items belonging to the viewer's own panel get a subtle gold border
          // so chef immediately sees "this row is mine".
          const containsMine = Object.keys(groups).some(k => {
            if (myPanelKey === 'kitchen' && k === 'food') return true;
            if (myPanelKey === 'bar' && k === 'drinks') return true;
            if (myPanelKey === 'shisha' && k === 'shisha') return true;
            if (myPanelKey === 'serving' && (k === 'activities' || order.items.some(i => i.status === 'ready'))) return true;
            return false;
          });
          return (
            <div
              key={order.id}
              className={`bg-white/[0.03] rounded-sm card-hover overflow-hidden border ${containsMine ? 'border-gold/30' : 'border-white/5'}`}
              data-testid={`all-orders-card-${order.id}`}
            >
              <div className="px-4 py-3 flex items-center justify-between border-b border-white/5">
                <div className="flex items-center gap-2 min-w-0 flex-wrap">
                  {order.is_takeaway ? (
                    <span className="text-[10px] uppercase tracking-[0.18em] bg-orange-500/15 text-orange-400 border border-orange-500/40 px-2 py-0.5 rounded-sm font-sans font-bold">Takeaway</span>
                  ) : (
                    <span className="text-gold font-serif text-lg font-semibold">{tableLabel(order.table_number)}</span>
                  )}
                  <span className="text-xs text-zinc-600 font-sans">#{order.id.slice(0, 6).toUpperCase()}</span>
                  {order.customer_name && <span className="text-xs text-zinc-400 font-sans truncate">· {order.customer_name}</span>}
                </div>
                <span className="text-xs text-zinc-500 font-mono flex items-center gap-1">
                  <Clock className="w-3 h-3" /> {getElapsedMinutes(order.created_at)}m
                </span>
              </div>
              <div className="p-3 space-y-2">
                {Object.entries(groups).map(([k, items]) => {
                  const meta = CAT_META[k] || CAT_META.other;
                  const Icon = meta.Icon;
                  return (
                    <div key={k} className={`rounded-sm border ${meta.bg} p-2`}>
                      <div className={`text-[10px] uppercase tracking-[0.18em] font-sans font-bold flex items-center gap-1.5 ${meta.color} mb-1.5`}>
                        <Icon className="w-3 h-3" /> {meta.label}
                      </div>
                      {items.map(it => {
                        const stMeta = STATUS_META[it.status] || STATUS_META.received;
                        const done = it.status === 'served';
                        return (
                          <div key={it.id} className="flex items-center justify-between gap-2 py-0.5">
                            <span className={`text-xs font-sans truncate ${done ? 'line-through text-zinc-500' : 'text-zinc-200'}`}>
                              {it.quantity}× {it.name}
                            </span>
                            <Badge className={`shrink-0 border-0 font-sans text-[10px] ${stMeta.cls}`}>
                              {done ? <Check className="w-3 h-3 mr-0.5" /> : null}
                              {stMeta.label}
                            </Badge>
                          </div>
                        );
                      })}
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
